import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/navigation';
import BlogAuthor from './components/BlogAuthor';
import BlogHeader from './components/BlogHeader';
import { Container } from './components/Container';
import { GetStartedBanner } from './components/GetStartedBanner';
import { Prose } from './components/Prose';
import RelatedBlogs from './components/RelatedBlogs';
import Footer from '@/components/bottom/Footer';
import { NavHeader } from '../navbar/NavHeader';
import { AuthProvider } from '@/contexts/AuthContext';
import { usePath } from '@/contexts/PathContext';
import { t } from 'i18next';

function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
      <path
        d="M7.25 11.25 3.75 8m0 0 3.5-3.25M3.75 8h8.5"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUpIcon(props) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
      <path
        d="M11.25 7.25 8 3.75m0 0-3.25 3.5M8 3.75v8.5"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BlogLayout({ articles, article, currentUser, children }) {
  const router = useRouter();
  const { previousPathname } = usePath();
  const [showScrollUpButton, setShowScrollUpButton] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);

  const blog = articles.find(
    (blog) =>
      blog.title == article.title &&
      blog.date === article.date &&
      blog.description == article.description,
  );

  useEffect(() => {
    function clamp(number, a, b) {
      let min = Math.min(a, b);
      let max = Math.max(a, b);
      return Math.min(Math.max(number, min), max);
    }
    const handleScroll = () => {
      let scrollY = clamp(
        window.scrollY,
        0,
        document.body.scrollHeight - window.innerHeight,
      );
      setShowScrollUpButton(scrollY < prevScrollPos && scrollY > 64);
      setPrevScrollPos(scrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prevScrollPos]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{blog.title}</title>
        <meta name="description" content={blog.description} />
        <meta name="author" content={blog.author} />
        <meta
          name="keywords"
          content={`${t(blog.tagLink)},${blog.hashtags?.join(',')}`}
        />
        <meta name="publisher" content={blog.author} />
        <meta property="og:title" content={blog.title} />
        <meta
          property="og:url"
          content={`https://medical.chat-data.com/blog/${blog.slug}`}
        />
        <link
          rel="canonical"
          href={`https://medical.chat-data.com/blog/${blog.slug}`}
        />
        <meta
          property="og:image"
          content={`https://medical.chat-data.com/${blog.thumbnail.src}`}
        />
        <meta property="og:image:width" content={blog.thumbnail.width} />
        <meta property="og:image:height" content={blog.thumbnail.height} />
        <meta property="og:image:alt" content={blog.title} />
        <meta property="og:description" content={blog.description} />
        <meta property="og:type" content="article" />
        <meta property="article:tag" content={t(blog.tagLink)} />
        {blog.hashtags?.map((tag, index) => (
          <meta key={index} property="article:tag" content={tag} />
        ))}

        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.description} />
        <meta
          name="twitter:image"
          content={`https://medical.chat-data.com/${blog.thumbnail.src}`}
        />
        <meta name="twitter:image:width" content={blog.thumbnail.width} />
        <meta name="twitter:image:height" content={blog.thumbnail.height} />
        <meta name="twitter:image:alt" content={blog.title} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'BlogPosting',
                  '@id': `https://medical.chat-data.com/blog/${blog.slug}`,
                  name: blog.title,
                  headline: blog.title,
                  author: {
                    '@type': 'Person',
                    name: blog.author,
                  },
                  publisher: {
                    '@id': 'https://medical.chat-data.com/#organization',
                  },
                  image: {
                    '@type': 'ImageObject',
                    url: `https://medical.chat-data.com/${blog.thumbnail.src}`,
                    width: blog.thumbnail.width,
                    height: blog.thumbnail.height,
                    caption: blog.title,
                  },
                  datePublished: `${blog.date}T11:10:24+00:00`,
                  dateModified: `${blog.date}T11:10:24+00:00`,
                  inLanguage: 'en-US',
                  mainEntityOfPage: {
                    '@id': `https://medical.chat-data.com/blog/${blog.slug}/#webpage`,
                  },
                  isPartOf: {
                    '@id': `https://medical.chat-data.com/blog/${blog.slug}/#webpage`,
                  },
                  articleSection: `Medical Chat, ${t(blog.tagLink)}}`,
                },
                {
                  '@type': 'BreadcrumbList',
                  '@id': `https://medical.chat-data.com/blog/${blog.slug}/#breadcrumblist`,
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      '@id': 'https://medical.chat-data.com/#listItem',
                      position: 1,
                      name: 'Medical Chat | Medical AI Assistant',
                      item: 'https://medical.chat-data.com/',
                      nextItem: {
                        '@id': `https://medical.chat-data.com/blog/#listItem`,
                      },
                    },
                    {
                      '@type': 'ListItem',
                      '@id': `https://medical.chat-data.com/blog/#listItem`,
                      position: 2,
                      name: 'Medical Chat Insights & Use Cases Blog | Discover & Learn',
                      item: 'https://medical.chat-data.com/blog/',
                      previousItem: {
                        '@id': 'https://medical.chat-data.com/#listItem',
                      },
                      nextItem: {
                        '@id': `https://medical.chat-data.com/blog/${blog.slug}/#listItem`,
                      },
                    },
                    {
                      '@type': 'ListItem',
                      '@id': `https://medical.chat-data.com/blog/${blog.slug}/#listItem`,
                      position: 3,
                      name: blog.title,
                      previousItem: {
                        '@id': 'https://medical.chat-data.com/blog/#listItem',
                      },
                    },
                  ],
                },
                {
                  '@type': 'WebPage',
                  '@id': `https://medical.chat-data.com/blog/${blog.slug}/#webpage`,
                  url: `https://medical.chat-data.com/blog/${blog.slug}`,
                  name: blog.title,
                  description: blog.description,
                  inLanguage: 'en-US',
                  isPartOf: {
                    '@id': 'https://medical.chat-data.com/#website',
                  },
                  breadcrumb: {
                    '@id': `https://medical.chat-data.com/blog/${blog.slug}/#breadcrumblist`,
                  },
                  datePublished: `${blog.date}T11:10:24+00:00`,
                  dateModified: `${blog.date}T11:10:24+00:00`,
                },
              ],
            }),
          }}
        ></script>
      </Head>
      <AuthProvider user={currentUser}>
        <NavHeader />
      </AuthProvider>
      <Container>
        <div className="xl:relative ">
          <div className="mx-auto max-w-5xl">
            <BlogHeader article={blog} />
            {previousPathname && (
              <button
                type="button"
                onClick={() => router.back()}
                aria-label="Go back to articles"
                className="group mb-8 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md shadow-zinc-800/5 ring-1 ring-zinc-900/5 transition lg:absolute lg:-left-5 lg:-mt-2 lg:mb-0 xl:-top-1.5 xl:left-0 xl:mt-0 hover:scale-125 dark:border dark:border-zinc-700/50 dark:bg-zinc-800 dark:ring-0 dark:ring-white/10 dark:hover:border-zinc-700 dark:hover:ring-white/20"
              >
                <ArrowLeftIcon className="h-4 w-4 stroke-zinc-500 transition group-hover:stroke-zinc-700 dark:stroke-zinc-500 dark:group-hover:stroke-zinc-400" />
              </button>
            )}
            {showScrollUpButton && (
              <button
                type="button"
                onClick={scrollToTop}
                aria-label="Scroll up to the top of the page"
                className="z-10 fixed bottom-4 group mb-8 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md shadow-zinc-800/5 ring-1 ring-zinc-900/5 transition  hover:scale-125 dark:border dark:border-zinc-700/50 dark:bg-zinc-800 dark:ring-0 dark:ring-white/10 dark:hover:border-zinc-700 dark:hover:ring-white/20 lg:transform lg:translate-x-[-80px]"
              >
                <ArrowUpIcon className="h-4 w-4 stroke-zinc-500 transition group-hover:stroke-zinc-700 dark:stroke-zinc-500 dark:group-hover:stroke-zinc-400" />
              </button>
            )}
            <article className="mt-8 lg:mt-16">
              <header className="flex flex-col">
                <h1 className="mt-6 text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl md:text-4xl lg:text-5xl dark:text-zinc-100">
                  {blog.title}
                </h1>
              </header>
              <BlogAuthor article={blog} />
              <Prose className="my-8" data-mdx-content>
                {children}
              </Prose>
            </article>
            <RelatedBlogs article={blog} articles={articles} />
          </div>
        </div>
        <GetStartedBanner />
      </Container>
      <Footer />
    </>
  );
}
